import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/ui';
import { usePersistentState } from '@/lib/prefs';
import { TitleBar } from './TitleBar';
import { Sidebar } from './Sidebar';
import { findNavItem } from './navigation';
import { track } from '@/lib/analytics';

/**
 * The application frame: top bar, sidebar, action bar, workspace.
 *
 * Exactly one element in this app scrolls — `<main>` below. The body is locked
 * (see tokens.css) so the chrome never moves and a long register scrolls
 * underneath a fixed header. Pages should assume they are handed a flex
 * column with a fixed height, not an infinitely tall document.
 */
export function AppLayout({ onSignOut }: { onSignOut: () => void }) {
  /* A preference, not a location: it is the same on every screen and belongs to
     this machine rather than to a link, so it persists locally instead of
     going in the URL. See the distinction at the top of src/lib/prefs.ts. */
  const [collapsed, setCollapsed] = usePersistentState('sidebar.collapsed', false);
  const { t } = useTranslation();
  const location = useLocation();

  const item = findNavItem(location.pathname);
  const pageTitle = item ? t(item.titleKey) : t('pageTitles.dashboard');

  /* One clean navigation event, keyed on the nav id — `students`, `collection`.
     This is what replaces autocapture's pageviews: a stable name that survives
     a route rename, and no query string, which on these screens carries filter
     values and sometimes a student id. */
  /* Only a screen that resolved to a nav entry is a screen. `/` and any
     unmatched path render a `<Navigate>` to the dashboard from inside this
     layout, so the effect ran once against a pathname that matches nothing on
     the way through — which made `unknown` one of the most-viewed screens in
     the report, on every cold start. */
  const screen = item?.id;
  useEffect(() => {
    if (screen) track('Screen Viewed', { screen });
  }, [screen]);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-canvas">
      <TitleBar />

      <div className="flex min-h-0 flex-1">
        <Sidebar
          collapsed={collapsed}
          onToggleCollapsed={() => setCollapsed((c) => !c)}
          onSignOut={onSignOut}
        />

        <div className="flex min-w-0 flex-1 flex-col">
          {/* ── Action bar ────────────────────────────────────────────────────
              Breadcrumbs only. v1 moved this bar's controls up into the title
              bar and left the trail behind; keeping it that way means the
              user's eye never has to re-find them between screens. */}
          <div className="flex h-actionbar shrink-0 items-center border-b border-line-soft bg-surface px-5">
            <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs">
              <span className="text-muted">Gurukul</span>
              <Icon name="chevronRight" size={13} className="text-line-strong" />
              <span className="font-semibold text-ink">{pageTitle}</span>
            </nav>
          </div>

          {/* The app's only scroll container. v1 padded the workspace 28px/32px. */}
          <main className="min-h-0 flex-1 overflow-y-auto">
            {/* `h-full`, not `min-h-full`. A minimum is a floor and permits
                growth, so the wrapper sized itself to its content and every
                `flex-1 min-h-0` beneath it resolved against *that* rather than
                against the viewport — the same as no height constraint at all.
                Panes could not shrink, so nothing scrolled inside them and the
                workspace scrolled instead, carrying a split screen's detail
                pane off the top while you scrolled its list.

                This is a ceiling flex children may still exceed when they
                genuinely cannot shrink, and `main` keeps `overflow-y-auto` so
                those still scroll. What changed is that a pane which *can*
                shrink now does. */}
            <div className="flex h-full flex-col px-workspace-x py-workspace-y">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
